import { debounce } from 'lodash'
import {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Dimensions, FlatList, Pressable, View } from 'react-native'
import {
  Button,
  DataTable,
  Icon,
  Modal,
  Portal,
  Searchbar,
  Text,
} from 'react-native-paper'
import Toast from 'react-native-root-toast'
import { AuthContext } from '../../../../../contexts/auth-context'
import { ConfirmDialogue, ConfirmDialogueRef } from '../../../../../@ui'
import Row from '../../../../../@ui/containers/grid/row'
import { TitledTable } from '../../../../../@ui'
import {
  SearchUserQuery,
  TransferArticleOwnershipInput,
  useArticleQuery,
  useSearchUserLazyQuery,
  useTransferArticleOwnershipMutation,
} from '../../../../../generated/graphql'
import { useTheme } from '../../../../../hooks/theme.hook'
import { useIsMobileDevice } from '../../../../../hooks/is-mobile.hook'
import { TouchableIconWithToolTip } from '../../../../../utils/meldd-tooltip'
import { MessageType } from '../../../../../utils/message-type'
import { Unpacked } from '../../../../../utils/types'
import * as Sentry from '@sentry/react-native'

interface Props {
  articleId: string
  onTransferSuccess?: () => void
}

type ListUser = Unpacked<SearchUserQuery['usersBySearchUsername']>
type TransferCandidatesModalProps = {
  visible: boolean
  nodeId?: string
  currentUsername?: string | null
  theme: ReturnType<typeof useTheme>
  searchByUsername: ReturnType<typeof useSearchUserLazyQuery>[0]
  onDismiss: () => void
  onSelectUser: (userId: string) => void
}

function TransferCandidatesModal({
  visible,
  nodeId,
  currentUsername,
  theme,
  searchByUsername,
  onDismiss,
  onSelectUser,
}: TransferCandidatesModalProps) {
  const { t } = useTranslation('articles')
  const [search, setSearch] = useState('')
  const [searchResult, setSearchResult] = useState<ListUser[]>([])
  const isMobileDevice = useIsMobileDevice()
  const windowWidth = Dimensions.get('window').width
  const modalHeight = Dimensions.get('window').height * 0.6
  const transferCandidateList = useMemo(
    () => searchResult.filter((item) => Boolean(item.id)),
    [searchResult]
  )
  const isEmptySearch = search.trim().length === 0
  const searchUsers = useCallback(
    debounce(async (input: string) => {
      try {
        const trimmedInput = input.trim()
        if (trimmedInput.length < 1) {
          setSearchResult([])
          return
        }
        const query = await searchByUsername({
          variables: {
            input: {
              username: trimmedInput.toLowerCase(),
              nodeId,
            },
          },
          fetchPolicy: 'network-only',
        })
        setSearchResult(query.data?.usersBySearchUsername ?? [])
      } catch (err) {
        setSearchResult([])
        console.error(err)
        Sentry.captureException(err)
      }
    }, 100),
    [nodeId, searchByUsername]
  )
  useEffect(() => {
    return () => {
      searchUsers.cancel()
    }
  }, [searchUsers])

  useEffect(() => {
    if (!visible) {
      setSearch('')
      setSearchResult([])
    }
  }, [visible])

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        contentContainerStyle={{
          backgroundColor: theme.colors.surface,
          margin: theme.spacing(2),
          padding: theme.spacing(2),
          height: modalHeight,
          width: windowWidth - 2 * theme.spacing(2),
          maxWidth: isMobileDevice ? undefined : 520,
          alignSelf: 'center',
        }}
      >
        <View style={{ flex: 1 }}>
          <Row
            justifyContent="flex-end"
            style={{ marginBottom: theme.spacing(1) }}
          >
            <Button mode="text" compact onPress={onDismiss}>
              {t('cancel')}
            </Button>
          </Row>
          <Searchbar
            placeholder={t('search')}
            onChangeText={(input) => {
              setSearch(input)
              searchUsers(input)
            }}
            value={search}
            style={{ marginBottom: theme.spacing(2) }}
          />
          <FlatList
            data={transferCandidateList}
            keyExtractor={(item) => item.id || item.username || ''}
            keyboardShouldPersistTaps="handled"
            ItemSeparatorComponent={() => (
              <View style={{ height: theme.spacing(1) }} />
            )}
            renderItem={({ item }) => {
              const isCurrentUser = item.username === currentUsername
              return (
                <Pressable
                  disabled={isCurrentUser}
                  onPress={() => item.id && onSelectUser(item.id)}
                  style={({ pressed }) => ({
                    opacity: isCurrentUser ? 0.4 : 1,
                    paddingVertical: theme.spacing(3),
                    paddingHorizontal: theme.spacing(3),
                    borderRadius: theme.spacing(1),
                    backgroundColor: pressed
                      ? theme.colors.surfaceVariant
                      : theme.colors.surface,
                  })}
                >
                  <Text>{item.username || ''}</Text>
                </Pressable>
              )
            }}
            ListEmptyComponent={
              <Text
                style={{
                  color: theme.colors.onSurfaceDisabled,
                  textAlign: 'center',
                }}
              >
                {isEmptySearch
                  ? t('start-typing-user-search')
                  : t('no-results')}
              </Text>
            }
          />
        </View>
      </Modal>
    </Portal>
  )
}

export function TransferOwnership(props: Props) {
  const { articleId, onTransferSuccess } = props
  const { t } = useTranslation('articles')
  const theme = useTheme()

  const user = useContext(AuthContext)

  const [showSearch, setShowSearch] = useState(false)
  const [pendingUserId, setPendingUserId] = useState<string | null>(null)

  const confirmDialogueRef = useRef<ConfirmDialogueRef>(null)

  const [searchByUsername] = useSearchUserLazyQuery()
  const [transferArticleOwnership] = useTransferArticleOwnershipMutation()

  const { data: articleData } = useArticleQuery({
    variables: { id: articleId },
  })

  const clearInput = useCallback(() => {
    setShowSearch(false)
  }, [])

  const showConfirmDialogue = useCallback(
    (userId: string) => {
      setPendingUserId(userId)
      clearInput()
      confirmDialogueRef.current?.open()
    },
    [clearInput]
  )

  const handleConfirmTransfer = useCallback(async () => {
    if (!pendingUserId || !articleId) {
      return
    }
    const input: TransferArticleOwnershipInput = {
      articleId,
      newOwnerId: pendingUserId,
    }
    try {
      const result = await transferArticleOwnership({ variables: { input } })
      if (result.data) {
        Toast.show(t('transfer-success'))
        onTransferSuccess?.()
      }
    } catch (err) {
      Toast.show((err as Error).message, MessageType.error)
      console.error(err)
      Sentry.captureException(err)
    } finally {
      // Clean up state
      setPendingUserId(null)
      clearInput()
      confirmDialogueRef.current?.close()
    }
  }, [pendingUserId, articleId, transferArticleOwnership, clearInput, t])

  return (
    <>
      <TitledTable
        header={
          <Row justifyContent="space-between" flex={1}>
            <DataTable.Title>{t('transfer-ownership')}</DataTable.Title>
            <TouchableIconWithToolTip
              icon={<Icon source="plus-circle-outline" size={20} />}
              tooltip={t('add-member')}
              onPress={() => setShowSearch(true)}
            />
          </Row>
        }
      ></TitledTable>
      <TransferCandidatesModal
        visible={showSearch}
        nodeId={articleData?.article.cluster.nodeId}
        currentUsername={user.username}
        theme={theme}
        searchByUsername={searchByUsername}
        onDismiss={clearInput}
        onSelectUser={showConfirmDialogue}
      />
      <ConfirmDialogue
        ref={confirmDialogueRef}
        onContinue={handleConfirmTransfer}
        label={t('transfer-warning-title')}
      />
    </>
  )
}

export default TransferOwnership
